
// Packages
import { defineComponent } from 'vue';
import { isLoungePortal } from '@white-label-helper/shared-helpers';

// Constants
import {
  PROGRESS_INDICATOR_PAGES,
  LOUNGE_PORTAL_PROGRESS_INDICATOR_PAGES,
  MY_BOOKINGS_REDIRECTION_PAGES,
  ProductType,
  APP_HERO_PRODUCTS,
  FEATURE_FLAGS,
  BOOKING_PORTAL_PROGRESS_INDICATOR_PAGES,
} from '@white-label-configuration/constants';

// Helpers
import {
  getDomainUrl,
  DOMAINS_NAMES,
  ACCOUNT_URLS,
} from '@white-label-helper/switch-app';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { usePartnerConfigHelper } from '@white-label-helper/shared-helpers';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';


// Stores
import { mapState } from 'pinia';
import { useDeepLink } from '@white-label-store/deep-link';
import { useSearchResultsStore } from '@white-label-store/search-results';

// Types
import type { TabData } from '@white-label-types/navbar';

// Components
import Navbar from '../navbar/navbar.vue';
import ProgressIndicator from '../progress-indicator/progress-indicator.vue';

type RedirectionPagesKeys = keyof typeof MY_BOOKINGS_REDIRECTION_PAGES;
type RedirectionPagesValues =
  (typeof MY_BOOKINGS_REDIRECTION_PAGES)[RedirectionPagesKeys];

export default defineComponent({
  name: 'EcommerceNavbar',

  components: {
    Navbar,
    ProgressIndicator,
  },

  data() {
    const {
      isCrossSellFlowEnabled,
      isExtrasProductFlowEnabled,
    } = usePartnerConfigHelper();

    return {
      PROGRESS_INDICATOR_PAGES,
      isParkingProduct: getAppHeroProduct('lounges') === 'parking',
      isTravelInsuranceProduct: getAppHeroProduct('lounges') === 'travel_insurance',
      isCrossSellFlowEnabled,
      isExtrasProductFlowEnabled
    };
  },

  computed: {
    ...mapState(useDeepLink, ['discount']),
    ...mapState(useSearchResultsStore, [
      'inventoryMetadata',
      'hasExtrasSearchResults'
    ]),

    isLoungePortalPartner(): boolean {
      return isLoungePortal();
    },

    showLoginFlow(): boolean {
      return !isLoungePortal();
    },

    /**
     * Returns boolean based on if the $route.name exists in Indicators list of routes.
     * If in dedicated wl flow then removes 'dedicated-wl-' out from the route name,
     * so we can use normal route naming convention.
     */
    displayProgress(): boolean {
      const routeName =
        (getFeatureFlag(FEATURE_FLAGS.CUS_2533_DEDICATED_BOOKING_FLOW) && this.$route.name != null)
          ? this.$route.name.replace('dedicated-wl-', '')
          : this.$route.name;

      const result = Object.values(this.pages).includes(
        routeName as RedirectionPagesValues
      );

      return result
    },

    pages(): typeof PROGRESS_INDICATOR_PAGES | typeof LOUNGE_PORTAL_PROGRESS_INDICATOR_PAGES | typeof BOOKING_PORTAL_PROGRESS_INDICATOR_PAGES{
      if(isLoungePortal()) {
        return  LOUNGE_PORTAL_PROGRESS_INDICATOR_PAGES
      }
      if(isBookingPortalFlow()) {
        return  BOOKING_PORTAL_PROGRESS_INDICATOR_PAGES
      }
      return PROGRESS_INDICATOR_PAGES;
    },

    productTypes() {
      return getAppVariable<ProductType[]>('product_types_available', []);
    },

    extrasPage(): string {
      let result = '';
      if (this.isCrossSellFlowEnabled) {
        result = PROGRESS_INDICATOR_PAGES.travel_extras;
      } else if (this.isExtrasProductFlowEnabled) {
        result = PROGRESS_INDICATOR_PAGES.extras_product;
      }
      return result;
    },

    shouldSkipExtrasStep(): boolean {
      const currentRouteIsExtrasPage = this.$route.name === this.extrasPage;
      // If no extras flow is enabled, skip
      if (!(this.isCrossSellFlowEnabled || this.isExtrasProductFlowEnabled)) {
        return true;
      // If we are on an extras page, don't skip
      } else if (currentRouteIsExtrasPage) {
        return false;

      // If travel extras is enabled:
      // - skip if content has finished loading
      // - AND we are on the search page
      // - AND we don't have extras to display
      } else if (this.isCrossSellFlowEnabled) {
        return !this.$contentPreloaderEnabled
          && this.$route.name === PROGRESS_INDICATOR_PAGES.choose
          && !this.hasExtrasSearchResults;

      // If the extras product flow is enabled, don't skip
      } else if (this.isExtrasProductFlowEnabled) {
        return false;
      }

      // Otherwise, skip
      return true;
    },

    tabData(): TabData {
      const domainUrl = getDomainUrl(DOMAINS_NAMES.ACCOUNT);
      const bookingLink = domainUrl ? domainUrl.concat(ACCOUNT_URLS.HOME) : '#';

      if (this.isParkingProduct && this.isBookingPortalFlowEnabled) {
        return {};
      }
      return {
        tabsLinks: {
          bookingLink: bookingLink,
        },
      };
    },

    partner_name() {
      return getAppVariable('partner_name');
    },

    discountQuery() {
      return (
        this.discount && { discount: this.discount }
      );
    },

    logoURL() {
      if(isLoungePortal()) {
        return '/images/lounge-portal-logo.svg'
      }
      return getAppVariable('logo');
    },

    logoRedirectURL() {
      if(isLoungePortal()) {
        return this.$auth.returnUrl;
      }
      return (
        getAppVariable('logo_redirect_url')
        || getDomainUrl(DOMAINS_NAMES.ECOMMERCE)
      );
    },
    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },
    homeRoute(): string {
      return this.pages.search;
    },
    searchRoute():string {
      if (this.isTravelInsuranceProduct) {
        // For when on Travel Insurance standalone flow
        return this.pages.quote;
      }
      return this.pages.choose;
    },
    checkoutRoute():string {
      return this.pages.checkout;
    },
  }
});
